import React from 'react';
import { Button } from '../components/ui/button';
import { Shield, Heart, Users, Umbrella, CheckCircle2, ArrowRight } from 'lucide-react';

const Insurance = () => {
  const benefits = [
    'Comprehensive life insurance coverage for income protection',
    'Flexible short term medical insurance solutions',
    'Family security planning to protect your loved ones',
    'Income replacement strategies for unexpected events',
    'Affordable coverage options tailored to your budget',
    'Expert guidance through the selection process'
  ];

  const coverage = [
    {
      icon: Heart,
      title: 'Life Insurance',
      description: 'Protect your family\'s financial future with comprehensive life insurance coverage that ensures they\'re taken care of no matter what.'
    },
    {
      icon: Umbrella,
      title: 'Short Term Medical',
      description: 'Bridge coverage gaps with short term medical insurance that provides essential protection during transitions or waiting periods.'
    },
    {
      icon: Users,
      title: 'Family Protection',
      description: 'Comprehensive family coverage plans that ensure everyone you love has the protection they need.'
    },
    {
      icon: Shield,
      title: 'Income Protection',
      description: 'Safeguard your income and maintain your standard of living even when unexpected events occur.'
    }
  ];

  const whyChoose = [
    'Personalized coverage recommendations based on your unique needs',
    'Access to multiple insurance providers and options',
    'No-pressure consultation and education-first approach',
    'Ongoing support and policy reviews',
    'Help with claims and policy management',
    'Affordable solutions that fit your budget'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white p-6 rounded-full shadow-lg">
              <Shield className="h-16 w-16 text-[#0B8FD8]" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold mb-6">
            Life & Short Term Medical Insurance
          </h1>
          <p className="text-lg md:text-xl text-[#2C3E50] leading-relaxed mb-8">
            Protect what matters most with comprehensive life insurance and short term medical coverage 
            designed to provide security and peace of mind for you and your family.
          </p>
          <Button 
            className="bg-[#0B8FD8] hover:bg-[#0973B0] text-white px-8 py-4 text-lg rounded-md transition-all duration-300 shadow-lg hover:shadow-xl"
            onClick={() => window.open('https://go.sbrooksassociatesllc.com/bookwithshannon', '_blank')}
          >
            Get Your Free Quote
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Coverage Options Section */}
      <section className="bg-white py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-16">
            Coverage Options
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coverage.map((option, index) => {
              const Icon = option.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] rounded-xl p-8 space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="bg-white p-3 rounded-lg shadow-md">
                      <Icon className="h-8 w-8 text-[#0B8FD8]" />
                    </div>
                    <h3 className="text-2xl font-serif text-[#0A2540] font-bold">
                      {option.title}
                    </h3>
                  </div>
                  <p className="text-base text-[#2C3E50] leading-relaxed">
                    {option.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-12">
            Why Choose Our Insurance Services
          </h2>
          
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <CheckCircle2 className="h-6 w-6 text-[#0B8FD8] flex-shrink-0 mt-1" />
                  <p className="text-base text-[#2C3E50]">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-white py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-16">
            The Brooks & Associates Difference
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChoose.map((reason, index) => (
              <div key={index} className="bg-[#C4E5F4] rounded-xl p-6 flex items-start space-x-3">
                <CheckCircle2 className="h-6 w-6 text-[#0B8FD8] flex-shrink-0 mt-1" />
                <p className="text-base text-[#2C3E50] font-medium">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-12">
            Common Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-xl font-serif text-[#0A2540] font-bold mb-3">
                What is short term medical insurance?
              </h3>
              <p className="text-base text-[#2C3E50]">
                Short term medical insurance provides temporary health coverage during transitions such as job changes, 
                waiting periods for employer coverage, or gaps between insurance plans.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-xl font-serif text-[#0A2540] font-bold mb-3">
                How much life insurance do I need?
              </h3>
              <p className="text-base text-[#2C3E50]">
                The amount of life insurance you need depends on your income, debts, family size, and future financial goals. 
                We'll help you calculate the right coverage during your consultation.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-xl font-serif text-[#0A2540] font-bold mb-3">
                Is there a cost for the consultation?
              </h3>
              <p className="text-base text-[#2C3E50]">
                No! Your initial consultation is completely free with no obligation. We'll review your needs and 
                provide personalized recommendations at no cost.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0B8FD8] py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white font-bold mb-6">
            Protect Your Family's Future Today
          </h2>
          <p className="text-lg text-white mb-8">
            Get a free consultation and personalized insurance recommendations.
          </p>
          <Button 
            className="bg-white hover:bg-gray-100 text-[#0B8FD8] px-8 py-4 text-lg rounded-md transition-all duration-300 shadow-lg hover:shadow-xl"
            onClick={() => window.open('https://go.sbrooksassociatesllc.com/bookwithshannon', '_blank')}
          >
            Schedule Your Free Consultation
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Insurance;
